import type { DropdownOption } from '@/design-system';
import {
    FIELD_ROLES,
    sortRoles,
    worksInTheField,
    type Invitation,
    type TeamMember,
} from '@/entities/user';
import { ROLE_LABELS, type Role } from '@/shared/api';

import { inspectorPickerCopy as copy } from './copy';

/** Wie de keuze maakt: de persoon achter de sessie, of bij meekijken de ander. */
export interface PickerSelf {
    uuid: string | null;
    name: string | null;
    roles: readonly Role[];
}

export interface InspectorOptionsInput {
    self: PickerSelf;
    /** De actieve collega's (`GET /users?filter[status]=active`). */
    members: readonly TeamMember[];
    /** De uitnodigingen die nog openstaan (`GET /invitations`). */
    invitations: readonly Invitation[];
    /** Een filter begint met "alle"; een keuze voor één keuring niet. */
    allLabel?: string;
}

/** Een uitnodiging is geen persoon: haar waarde kan nooit een uuid van iemand zijn. */
const INVITATION_VALUE = 'uitnodiging:';

const byName = (left: string, right: string) => left.localeCompare(right, 'nl');

function rolesLabel(roles: readonly Role[]): string {
    return sortRoles(roles)
        .map((role) => ROLE_LABELS[role])
        .join(' · ');
}

/**
 * De opties van de inspecteurskeuze, in deze volgorde:
 *
 * 1. "Mijzelf", met je naam - ook als je nog geen inspecteur bent: de API neemt
 *    elk actief lid aan, en wie zichzelf kiest krijgt de vraag of hij ook als
 *    inspecteur wil werken;
 * 2. de collega's met de rol inspecteur, op naam, met hun rollen eronder - een
 *    eigenaar staat er dus alleen tussen als die ook inspecteur is;
 * 3. de openstaande uitnodigingen voor zo'n rol, uitgeschakeld: toewijzen kan pas
 *    als de uitnodiging is geaccepteerd, want de API vraagt een actief lid.
 */
export function inspectorOptions({
    self,
    members,
    invitations,
    allLabel,
}: InspectorOptionsInput): DropdownOption[] {
    const options: DropdownOption[] = [];

    if (allLabel !== undefined) {
        options.push({ value: '', label: allLabel });
    }

    if (self.uuid) {
        const name = self.name ?? '';

        options.push({
            value: self.uuid,
            label: copy.myself,
            hint: worksInTheField(self.roles) ? name : copy.myselfNotYet(name),
        });
    }

    [...members]
        .filter(
            (member) =>
                member.uuid !== self.uuid &&
                member.status === 'active' &&
                worksInTheField(member.roles),
        )
        .sort((left, right) => byName(left.name || left.email, right.name || right.email))
        .forEach((member) =>
            options.push({
                value: member.uuid,
                label: member.name || member.email,
                hint: rolesLabel(member.roles),
            }),
        );

    [...invitations]
        .filter(
            (invitation) =>
                !invitation.expired &&
                invitation.role !== null &&
                (FIELD_ROLES as readonly string[]).includes(invitation.role),
        )
        .sort((left, right) => byName(left.name ?? left.email, right.name ?? right.email))
        .forEach((invitation) =>
            options.push({
                value: `${INVITATION_VALUE}${invitation.uuid}`,
                label: invitation.name ?? invitation.email,
                hint: copy.invited,
                disabled: true,
            }),
        );

    return options;
}
